import { WiseAceLogo } from "@/components/WiseAceLogo";
import { StatusBadge } from "@/components/StatusBadge";
import { money, fmtDate } from "@/lib/format";
import { logoUrl } from "@/lib/api";

export function DocumentA4({ doc, settings, title, statusValue }) {
  const s = settings || {};
  const cust = doc.customer || {};
  const curr = s.currency || "R";
  const brandLogo = s.logo_path ? logoUrl() : s.logo_url;
  return (
    <div className="print-area max-w-[794px] mx-auto bg-white p-8 md:p-12 shadow-2xl rounded-sm border border-slate-200 my-6" data-testid="document-a4">
      {/* header */}
      <div className="flex justify-between items-start pb-6 border-b-2 border-[#0B132B]">
        <div>
          {brandLogo ? <img src={brandLogo} alt="Business logo" className="h-14 max-w-[220px] object-contain mb-2" data-testid="document-logo" /> : <WiseAceLogo size={44} />}
          <div className="mt-3 text-sm">
            <div className="font-semibold text-[#0B132B]">{s.company_name || "WiseAce Projects and Solutions Pty Ltd"}</div>
            {s.address && <div className="text-slate-500">{s.address}</div>}
            {s.email && <div className="text-slate-500">{s.email}</div>}
            {s.cell && <div className="text-slate-500">Cell: {s.cell}</div>}
            {s.whatsapp && <div className="text-slate-500">WhatsApp: {s.whatsapp}</div>}
            {s.vat_number && <div className="text-slate-500">VAT: {s.vat_number}</div>}
          </div>
        </div>
        <div className="text-right">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0B132B]">{title}</h1>
          <div className="text-[#D4AF37] font-mono font-semibold mt-1">{doc.number}</div>
          <div className="mt-2"><StatusBadge status={statusValue} /></div>
          <div className="text-xs text-slate-500 mt-3">
            <div>Issued: {fmtDate(doc.issue_date)}</div>
            {doc.due_date && <div>{title === "QUOTE" ? "Valid until" : "Due"}: {fmtDate(doc.due_date)}</div>}
          </div>
        </div>
      </div>

      {/* bill to */}
      <div className="py-5">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Bill To</div>
        <div className="text-sm mt-1">
          <div className="font-semibold text-[#0B132B]">{cust.name || "—"}</div>
          {cust.address && <div className="text-slate-500">{cust.address}</div>}
          {cust.email && <div className="text-slate-500">{cust.email}</div>}
          {cust.phone && <div className="text-slate-500">{cust.phone}</div>}
          {cust.vat_number && <div className="text-slate-500">VAT: {cust.vat_number}</div>}
        </div>
      </div>

      {/* items */}
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#0B132B] text-white text-left">
            <th className="px-3 py-2.5 font-semibold rounded-l">Description</th>
            <th className="px-3 py-2.5 font-semibold text-right">Qty</th>
            <th className="px-3 py-2.5 font-semibold text-right">Unit Price</th>
            <th className="px-3 py-2.5 font-semibold text-right">VAT</th>
            <th className="px-3 py-2.5 font-semibold text-right rounded-r">Amount</th>
          </tr>
        </thead>
        <tbody>
          {(doc.line_items || []).map((li, i) => (
            <tr key={i} className={i % 2 ? "bg-slate-50" : ""}>
              <td className="px-3 py-2.5 text-[#0B132B]">{li.description}</td>
              <td className="px-3 py-2.5 text-right text-slate-600">{li.quantity}</td>
              <td className="px-3 py-2.5 text-right text-slate-600">{money(li.unit_price, curr)}</td>
              <td className="px-3 py-2.5 text-right text-slate-600">{li.vat_rate}%</td>
              <td className="px-3 py-2.5 text-right font-medium text-[#0B132B]">{money((li.quantity || 0) * (li.unit_price || 0), curr)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* totals */}
      <div className="flex justify-end mt-5">
        <div className="w-64 text-sm space-y-1.5">
          <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="text-[#0B132B]">{money(doc.subtotal, curr)}</span></div>
          {doc.discount_amount > 0 && <div className="flex justify-between"><span className="text-slate-500">Discount</span><span className="text-rose-600">−{money(doc.discount_amount, curr)}</span></div>}
          <div className="flex justify-between"><span className="text-slate-500">VAT</span><span className="text-[#0B132B]">{money(doc.vat_amount, curr)}</span></div>
          <div className="flex justify-between pt-2 border-t-2 border-[#0B132B] text-base font-display font-bold text-[#0B132B]"><span>Total</span><span>{money(doc.total, curr)}</span></div>
          {typeof doc.amount_paid === "number" && doc.amount_paid > 0 && (
            <>
              <div className="flex justify-between text-emerald-600"><span>Paid</span><span>−{money(doc.amount_paid, curr)}</span></div>
              <div className="flex justify-between font-semibold text-[#0B132B]"><span>Balance Due</span><span>{money(Math.max(doc.total - doc.amount_paid, 0), curr)}</span></div>
            </>
          )}
        </div>
      </div>

      {/* footer */}
      <div className="mt-8 pt-5 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-slate-500">
        <div>
          {(s.bank_name || s.account_number) && (
            <>
              <div className="font-semibold text-[#0B132B] mb-1">Banking Details</div>
              {s.bank_name && <div>Bank: {s.bank_name}</div>}
              {s.account_name && <div>Account Name: {s.account_name}</div>}
              {s.account_number && <div>Account No: {s.account_number}</div>}
              {s.branch_code && <div>Branch Code: {s.branch_code}</div>}
            </>
          )}
        </div>
        <div>
          {doc.notes && <><div className="font-semibold text-[#0B132B] mb-1">Notes</div><div>{doc.notes}</div></>}
          {s.terms && <div className="mt-2">{s.terms}</div>}
        </div>
      </div>
      <div className="text-center text-[10px] text-slate-400 mt-6">{s.company_name || "WiseAce Projects and Solutions Pty Ltd"} · Generated by WiseAce Invoicing</div>
    </div>
  );
}
